import { required, maxLength, notEmail } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import { Box } from 'braid-design-system';
import debounce from 'lodash.debounce';
import React, {
  forwardRef,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import {
  JOB_DESCRIPTION_INTERACTION,
  JOB_DESCRIPTION_PRESSED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import {
  FEATURE_FILTER_CONTENT,
  FEATURE_PER_UNIT_BILLING,
  useFeatureToggleData,
} from 'src/app/shared/featureToggle';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import {
  type Validators,
  filterContent,
  maxEmojiLengthValidator,
  validateField,
} from 'src/app/utils/validation';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { sendContentSafetyTrackingEvents } from 'src/app/v1/dataAnalytics/utils/sendContentSafetyTrackingEvents';
import { sendMaxEmojiLengthTrackingEvents } from 'src/app/v1/dataAnalytics/utils/sendMaxEmojiLengthTrackingEvents';
import { sendPerUnitBillingValidationTrackingEvents } from 'src/app/v1/dataAnalytics/utils/sendPerUnitBillingValidationTrackingEvents';
import { actionUpdateJobDescription } from 'src/app/v1/state/modules/formData/jobDescription';
import { RichTextEditor } from 'src/app/v1/views/components/RichTextEditor';
import { useEmojiRestrictionsFeature } from 'src/app/v1/views/hooks/useEmojiRestrictionsFeature';
import type { AppState } from 'src/types/appState';
import type { StandardWritingFlowRef } from 'src/types/formFields';
import type { TrackingEvent } from 'src/types/trackingData';

import {
  DEBOUNCE_TIME,
  DEBOUNCE_CHAR_LENGTH,
} from '../../../../shared/constants/jobDescription';

import translations from './.vocab';

const MAX_CHARACTER_LIMIT = 15_000;
export const MAX_EMOJI_LENGTH = 10;

export const StandardWritingFlow = forwardRef(
  (_props, ref: Ref<StandardWritingFlowRef>) => {
    const { jobDescription, isEsmAccount, jobLocationCountry, hirerNation } =
      useSelector(
        (state: AppState) => ({
          jobDescription: state?.formData?.jobDescription,
          isEsmAccount: state.session.hirerAccount.isEsmAccount ?? undefined,
          jobLocationCountry: state.session.jobLocationCountry,
          hirerNation: state.session?.hirerAccount?.nation,
        }),
        shallowEqual,
      );
    const country = (jobLocationCountry ?? hirerNation ?? 'AU').toLowerCase();
    const { t } = useTranslations(translations);
    const isFilterContentEnabled = useFeatureToggleData(FEATURE_FILTER_CONTENT);
    const language = useConfig().getLanguage();

    const [inputValue, setInputValue] = useState(jobDescription);

    const dispatch = useDispatch();
    const requiredLabel = t('job-description-required-label');
    const [validationMessage, setValidationMessage] = useState<string | null>(
      null,
    );
    const [hasInteracted, setHasInteracted] = useState<boolean>(false);
    const isPerUnitBilling = useFeatureToggleData(FEATURE_PER_UNIT_BILLING);
    const { shouldRestrictEmojis } = useEmojiRestrictionsFeature();

    useImperativeHandle(ref, () => ({ handleValidation, inputValue }));

    const handleValidation = useCallback(
      (skipIfNull = false, checkRequired = true): string | null => {
        let validators: Validators = [maxLength, filterContent];
        if (checkRequired) validators = [...validators, required];
        if (isPerUnitBilling) validators = [...validators, notEmail];
        if (shouldRestrictEmojis)
          validators = [...validators, maxEmojiLengthValidator];
        if (!isFilterContentEnabled || !Boolean(inputValue))
          validators = validators.filter(
            (validator) => validator.name !== 'filterContent',
          );
        const validationResult = validateField(
          {
            field: 'jobDescription',
            validators,
            validatorProps: {
              max: MAX_CHARACTER_LIMIT,
              maxEmojiLength: MAX_EMOJI_LENGTH,
              requiredLabel,
              country,
              sendTrackingEvents: sendContentSafetyTrackingEvents,
            },
            value: inputValue,
          },
          skipIfNull,
          language,
          [
            sendPerUnitBillingValidationTrackingEvents,
            sendMaxEmojiLengthTrackingEvents,
          ],
        );

        setValidationMessage(validationResult);

        return validationResult;
      },
      [
        isPerUnitBilling,
        shouldRestrictEmojis,
        isFilterContentEnabled,
        inputValue,
        requiredLabel,
        country,
        language,
      ],
    );

    const onChange = (value: string) => {
      setHasInteracted(true);
      setInputValue(value);
    };

    const debounceOnChange = debounce(onChange, DEBOUNCE_TIME);

    const handleChange = useCallback(
      (value: string) => {
        const isLargeText = value.length > DEBOUNCE_CHAR_LENGTH;
        const onChangeMethod = isLargeText ? debounceOnChange : onChange;
        onChangeMethod(value);
      },
      [debounceOnChange],
    );

    const handleOnBlur = () => {
      setHasInteracted(true);

      const eventData: TrackingEvent = {
        eventName: JOB_DESCRIPTION_PRESSED,
        objectInteraction: JOB_DESCRIPTION_INTERACTION,
      };

      getTrackingAdapter.sendLinkEvent(eventData);
      debounceOnChange.flush();
    };

    useEffect(() => {
      const validationResult = handleValidation(false, hasInteracted);

      if (!validationResult) {
        dispatch(actionUpdateJobDescription(inputValue));
      }
    }, [handleValidation, dispatch, inputValue, hasInteracted]);

    return (
      <Box
        data-cy="standardWritingFlow"
        data-intercomid="jobDescriptionTextArea"
      >
        <RichTextEditor
          characterLimit={MAX_CHARACTER_LIMIT}
          message={validationMessage}
          valid={!validationMessage}
          value={inputValue}
          onBlur={handleOnBlur}
          onChange={handleChange}
          enableHyperlinks={isEsmAccount}
        />
      </Box>
    );
  },
);
